import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {getBrands, type Brand} from "@corporatesclub/shared";
import {useI18n} from "./I18nProvider";

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ#".split("");

function brandLetter(name: string): string {
  const raw = name.trim().charAt(0);
  if(!raw) return "#";
  const norm = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  if(/[A-Z]/.test(norm)) return norm;
  if(/[0-9]/.test(raw)) return "#";
  return "#";
}

function brandInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if(parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}

function groupBrands(items: Brand[]) {
  const map = new Map<string, Brand[]>();
  for(const b of items) {
    const letter = brandLetter(b.nume);
    if(!map.has(letter)) map.set(letter, []);
    map.get(letter)!.push(b);
  }
  const letters = [...map.keys()].sort((a, b) => {
    if(a === "#") return 1;
    if(b === "#") return -1;
    return a.localeCompare(b);
  });
  return letters.map((letter) => ({letter, brands: map.get(letter)!}));
}

export function BrandListView() {
  const {t} = useI18n();
  const [items, setItems] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeLetter, setActiveLetter] = useState("");
  useEffect(() => {
    getBrands().then((r) => setItems(r.items)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if(!s) return items;
    return items.filter((b) => b.nume.toLowerCase().includes(s));
  }, [items, q]);
  const groups = useMemo(() => groupBrands(filtered), [filtered]);
  const activeLetters = useMemo(() => new Set(groups.map((g) => g.letter)), [groups]);
  useEffect(() => {
    if(q || !groups.length) return;
    const obs = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if(!visible.length) return;
      const id = visible[0].target.id.replace("cc-brand-", "");
      setActiveLetter(id);
    }, {rootMargin: "-35% 0px -55% 0px", threshold: 0});
    for(const g of groups) {
      const el = document.getElementById(`cc-brand-${g.letter}`);
      if(el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [groups, q]);
  function jumpTo(letter: string) {
    setActiveLetter(letter);
    const el = document.getElementById(`cc-brand-${letter}`);
    if(el) el.scrollIntoView({behavior: "smooth", block: "start"});
  }
  if(loading) {
    return (
      <div className="cc-brands cc-brands--loading">
        <div className="cc-brands__hero cc-brands__hero--skeleton" />
        <div className="cc-brands__skeletonGrid">
          {Array.from({length: 12}).map((_, i) => <div key={i} className="cc-brands__skeletonCard" />)}
        </div>
      </div>
    );
  }
  return (
    <div className="cc-brands">
      <header className="cc-brands__hero">
        <div className="cc-brands__heroInner">
          <div className="cc-brands__heroCopy">
            <h1 className="cc-brands__title">{t("Brands")}</h1>
            <p className="cc-brands__lead">{filtered.length} {t("brands")}</p>
          </div>
          <div className="cc-brands__heroSearch">
            <div className="cc-brands__search">
              <i className="fa fa-search cc-brands__searchIcon" aria-hidden="true" />
              <input
                type="search"
                className="cc-brands__searchInput"
                placeholder={t("Search for brand...")}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label={t("Search for brand...")}
              />
              {q ? (
                <button type="button" className="cc-brands__searchClear" onClick={() => setQ("")} aria-label={t("Remove")}>
                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </header>
      {!q && groups.length ? (
        <nav className="cc-brands__alpha" aria-label={t("Brands")}>
          <div className="cc-brands__alphaTrack">
            {ALPHA.map((letter) => {
              const on = activeLetters.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  type="button"
                  className={`cc-brands__alphaBtn${on ? "" : " isDisabled"}${isActive ? " isActive" : ""}`}
                  disabled={!on}
                  onClick={() => jumpTo(letter)}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </nav>
      ) : null}
      <div className="cc-brands__body">
        {!filtered.length ? (
          <div className="cc-brands__empty">
            <i className="fa fa-search cc-brands__emptyIcon" aria-hidden="true" />
            <p>{t("No brands found")}</p>
          </div>
        ) : groups.map((g) => (
          <section key={g.letter} id={`cc-brand-${g.letter}`} className="cc-brands__section">
            <span className="cc-brands__watermark" aria-hidden="true">{g.letter}</span>
            <div className="cc-brands__sectionHead">
              <h2 className="cc-brands__letter">{g.letter}</h2>
              <span className="cc-brands__sectionRule" aria-hidden="true" />
              <span className="cc-brands__sectionCount">{g.brands.length}</span>
            </div>
            <div className="cc-brands__grid">
              {g.brands.map((b, idx) => (
                <Link
                  key={b.id}
                  to={b.url}
                  className="cc-brands__card"
                  style={{animationDelay: `${Math.min(idx, 10) * 35}ms`}}
                >
                  <span className="cc-brands__monogram" aria-hidden="true">{brandInitials(b.nume)}</span>
                  <span className="cc-brands__cardBody">
                    <span className="cc-brands__cardName">{b.nume}</span>
                    <span className="cc-brands__cardStock">{b.produseStoc} {t("products in stock")}</span>
                  </span>
                  <span className="cc-brands__cardArrow" aria-hidden="true">
                    <i className="fa fa-arrow-right" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
